import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, valuesOf } from '../skillUsage';

export const cinematicCameraMoveSkill: CodeoSkill = {
    name: SKILL_NAMES.cinematicCameraMove,
    kind: 'codeo',
    description:
        'Move a virtual camera over the whole scene composition — pull back, settle, push in — with ' +
        'a slow organic drift so the frame never sits still; or zoom into one off-center element. One camera per scene.',
    documentUsesSkill: check((script) => {
        const scales = valuesOf(script, 'scale');
        return (
            scales.some((value) => value > 1) &&
            /transformOrigin|transform-origin|onUpdate/.test(script) &&
            valuesOf(script, 'duration').some((value) => value >= 1.2)
        );
    }),
    instructions: `# Cinematic Camera Move

A camera wrapper transforms the scene's entire composition as one viewpoint: a phased scale (pull-back → focus → push-in) plus a continuous, almost-imperceptible sine drift. The phasing creates pacing — anticipation, reveal, settle — and the drift keeps the frame alive between phases. This is the difference between a flat motion-graphic and something that feels shot. Distinct from \`${SKILL_NAMES.parallaxDepthDrift}\` (which only moves background layers); the camera here moves *everything* in the scene.

The camera wraps the scene's visible content in a single clipped wrapper and is the only thing transformed — the assets inside still carry their own entrance tweens.

## Markup

The camera wrapper has NO CSS \`transform\` of its own — its motion is written as a plain \`element.style.transform\` string from a per-frame callback (see below), not through GSAP transform-channel tweens on the element, so there is nothing for a CSS transform to conflict with.

\`\`\`html
<div id="stage" style="position: absolute; inset: 0; background: var(--l5-color-2, #101820);">
  <div id="camera" style="position: absolute; inset: 0; overflow: hidden; transform-origin: 50% 50%;">
    <div style="position: absolute; inset: 0; display: grid; place-items: center;">
      <h1 id="hero-line"
          style="margin: 0; text-align: center; font-family: var(--l5-font-0), sans-serif; font-size: 120px; font-weight: 800; color: var(--l5-color-0, #ffffff);">Built for speed</h1>
    </div>
  </div>
</div>
\`\`\`

## Timeline code

The phase scale is a plain value; the drift adds a small sine offset every frame. Both write the camera transform through one \`onUpdate\` so they compose into a single transform string (seek-safe — GSAP re-runs it on every scrub). The hero line reveals only after the initial pull-back settles, so it doesn't look like it's flying away while the camera retreats.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const camera = document.querySelector('#camera');
const phase = { scale: 0.92 };          // start pulled back
const drift = { p: 0 };
const DUR = 6, DRIFT_AMP_X = 6, DRIFT_AMP_Y = 3;

function writeCamera() {
    const dx = Math.sin(drift.p) * DRIFT_AMP_X;
    const dy = Math.sin(drift.p * 1.3) * DRIFT_AMP_Y; // 1.3 ratio avoids a mechanical diagonal
    camera.style.transform = \`scale(\${phase.scale}) translate(\${dx}px, \${dy}px)\`;
}

tl.to(phase, { scale: 1.0, duration: 1.4, ease: 'power3.out', onUpdate: writeCamera }, 0.6);   // settle to neutral
tl.fromTo('#hero-line', { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out' }, 1.8); // reveal once settled
tl.to(phase, { scale: 1.08, duration: 1.6, ease: 'power2.inOut', onUpdate: writeCamera }, 3.2); // slow push-in for the climax
tl.to(drift, { p: Math.PI * 2 * 2, duration: DUR, ease: 'none', onUpdate: writeCamera }, 0);    // 2 slow breaths across the scene

window.__timelines = { main: tl };
\`\`\`

Zoom into an off-center element (variant): scale the camera and counter-translate so the target lands at center. Measure once before the timeline runs —
\`const STAGE_W = 1920; const r = el.getBoundingClientRect(); const ox = (STAGE_W/2 - (r.left + r.width/2)) / phase.scale;\` — then add \`ox\`/\`oy\` to the translate inside \`writeCamera\`.

## Adapt

- **Phase scales**: pull-back 0.88–0.96, focus 0.98–1.02, push 1.04–1.15. Tighter spread = subtler; wider = more cinematic. Each later phase should settle *deeper* (longer duration or more out-easing).
- **Eases**: \`power2.inOut\` / \`power3.out\` only. Spring/\`back\`/\`elastic\` on a camera feels nauseating — never use them on the frame itself.
- **Drift**: 1–3 cycles across the scene; amplitude X 2–8px, Y 1–4px. If the drift reads as a discrete shake, it's too big.
- **Camera shake** (impact/panic): swap the drift tween for a short, higher-amplitude, higher-frequency burst over ~0.3s.
- **Phase patterns**: focus-in (back→neutral→push, default product reveal), dramatic-reveal (push→neutral→pull), bookend-pull (neutral→push→neutral for CTA emphasis then release).

## Pitfalls

- **\`overflow: hidden\` on the camera** is mandatory — any phase with scale < 1 reveals the content's edges, and without clipping they leak into the frame as visible defects.
- Put the scene's **background on the camera's parent/stage, not on the camera** — scaling a backgrounded camera reveals the void behind it.
- **\`transform-origin: 50% 50%\`** — an off-center origin makes phases drift unpredictably.
- The camera's transform is written by hand in \`onUpdate\`, never through a GSAP transform-channel tween on the element itself — that's what keeps it free of the transform-conflict rule even though it has no baked CSS transform to worry about either way.
- Hero content should reveal *after* the initial pull-back settles, or it looks like it's flying away while the camera retreats.
- One camera per scene. Nesting cameras, or applying the transform per-element, breaks the "single viewpoint" read and reintroduces parallax bugs.`,
};
